/**
 * Pages villes IDF (Phase 2) — 20 villes avec contenu local unique.
 * Évite le duplicate content : chaque ville a ses spécificités, quartiers, prix, diagnostics prioritaires.
 */

export interface CityData {
  slug: string;
  nom: string;
  deptCode: string;
  deptNom: string;
  deptSlug: string;
  h1: string;
  metaTitle: string;
  metaDesc: string;
  intro: string;
  parc: string;
  quartiers: string[];
  prixM2: string;
  diagnostics: string[];
  heroImage: string;
}

export const citiesData: CityData[] = [
  // ═════════ 92 — Hauts-de-Seine ═════════
  {
    slug: "boulogne-billancourt",
    nom: "Boulogne-Billancourt",
    deptCode: "92",
    deptNom: "Hauts-de-Seine",
    deptSlug: "hauts-de-seine-92",
    h1: "Diagnostic immobilier à Boulogne-Billancourt (92) — DPE, Pack Vente dès 130 €",
    metaTitle: "Diagnostic Immobilier Boulogne-Billancourt 92 | DPE Pack Vente | Up Diag",
    metaDesc:
      "Diagnostic immobilier à Boulogne-Billancourt (92100) : DPE, amiante, plomb, Pack Vente complet. Intervention 48h, rapport certifié. Dès 130 €.",
    intro:
      "Up Diag intervient à Boulogne-Billancourt, deuxième commune francilienne la plus peuplée après Paris. Nous réalisons tous les diagnostics immobiliers obligatoires (DPE, amiante, plomb, électricité, gaz, Loi Carrez) avec une expertise spécifique au parc boulonnais, très contrasté entre immeubles Art Déco, résidences 1970 et tours neuves du Trapèze.",
    parc: "Boulogne-Billancourt présente un parc immobilier exceptionnellement varié. L'héritage Art Déco et Haussmann des quartiers Rhin-et-Danube et République cohabite avec les grands ensembles des années 1960-1980 autour de la Porte de Saint-Cloud, et les résidences neuves de la ZAC Trapèze (ancien site Renault). Les constructions d'avant 1949 sont fréquentes dans le centre, imposant souvent le CREP plomb.",
    quartiers: ["Trapèze", "Rhin-et-Danube", "République - Point-du-Jour", "Silly - Gallieni", "Parchamp - Albert-Kahn"],
    prixM2: "8 700 €/m²",
    diagnostics: [
      "DPE : enjeu fort sur les immeubles 1960-1980 du quartier Porte de Saint-Cloud",
      "CREP plomb : systématique pour les immeubles avant 1949 du centre",
      "Amiante parties privatives : biens 1960-1997 (nombreuses copropriétés concernées)",
      "Pack Vente complet pour les ventes d'appartements anciens",
    ],
    heroImage:
      "/img/readdy/city-bb.jpg",
  },
  {
    slug: "nanterre",
    nom: "Nanterre",
    deptCode: "92",
    deptNom: "Hauts-de-Seine",
    deptSlug: "hauts-de-seine-92",
    h1: "Diagnostic immobilier à Nanterre (92) — DPE & Pack Vente IDF",
    metaTitle: "Diagnostic Immobilier Nanterre 92 | DPE La Défense | Up Diag",
    metaDesc:
      "Diagnostic immobilier Nanterre (92000) : DPE, amiante, plomb, élec/gaz. Préfecture des Hauts-de-Seine, proximité La Défense. Intervention 48h.",
    intro:
      "Préfecture des Hauts-de-Seine et limitrophe de La Défense, Nanterre concentre un parc immobilier varié : grands ensembles 1960-1980, zones pavillonnaires anciennes et résidences récentes. Up Diag intervient dans tous les quartiers de la ville avec un délai garanti sous 48h.",
    parc: "Nanterre compte près de 94 000 habitants et près de 40 000 logements. Le parc se répartit entre grandes copropriétés des Bois et de l'Université, pavillonnaire ancien du Petit-Nanterre et du Plateau, et logements récents des quartiers Parc et Terrasses. Les diagnostics varient fortement selon la typologie du bien.",
    quartiers: ["Plateau", "Bois", "Parc Sud", "Petit-Nanterre", "Terrasses"],
    prixM2: "5 400 €/m²",
    diagnostics: [
      "Amiante fréquent sur les immeubles 1960-1980 des Bois et Université",
      "DPE : grands enjeux sur le bâti collectif ancien",
      "Pack Location pour le marché locatif étudiant (université Paris Nanterre)",
      "Électricité / gaz obligatoire sur pavillonnaire ancien",
    ],
    heroImage:
      "/img/readdy/city-nanterre.jpg",
  },
  {
    slug: "courbevoie",
    nom: "Courbevoie",
    deptCode: "92",
    deptNom: "Hauts-de-Seine",
    deptSlug: "hauts-de-seine-92",
    h1: "Diagnostic immobilier à Courbevoie (92) — DPE & Pack Diagnostic",
    metaTitle: "Diagnostic Immobilier Courbevoie 92 | DPE La Défense | Up Diag",
    metaDesc:
      "Diagnostic immobilier à Courbevoie (92400) : DPE, amiante, plomb, Pack Vente. Proximité La Défense, résidences récentes et immeubles anciens. Dès 130 €.",
    intro:
      "Courbevoie, 86 000 habitants, mélange résidences récentes de La Défense (Faubourg de l'Arche) et immeubles haussmanniens du centre-ville. Up Diag intervient sur tous vos diagnostics immobiliers obligatoires à Courbevoie avec une parfaite maîtrise du contraste entre bâti ancien et logements récents.",
    parc: "Le parc courbevoisien est très hétérogène : immeubles haussmanniens du Village (carré Saint-Germain), copropriétés 1960-1980 de Bécon et Gambetta, et tours récentes du Faubourg de l'Arche. Cette diversité impose une connaissance fine du bâti pour optimiser chaque diagnostic.",
    quartiers: ["Village", "Faubourg de l'Arche", "Bécon", "Gambetta", "La Défense (quartier Courbevoie)"],
    prixM2: "9 200 €/m²",
    diagnostics: [
      "DPE : classements très variables selon le quartier",
      "CREP plomb : biens du Village (avant 1949)",
      "Amiante : copros Bécon et Gambetta (années 1960-1970)",
      "Pack Vente complet haut de gamme (résidences Faubourg de l'Arche)",
    ],
    heroImage:
      "/img/readdy/city-courbevoie.jpg",
  },
  {
    slug: "levallois-perret",
    nom: "Levallois-Perret",
    deptCode: "92",
    deptNom: "Hauts-de-Seine",
    deptSlug: "hauts-de-seine-92",
    h1: "Diagnostic immobilier à Levallois-Perret (92) — DPE & Pack Vente premium",
    metaTitle: "Diagnostic Immobilier Levallois-Perret 92 | DPE Haut de gamme | Up Diag",
    metaDesc:
      "Diagnostic immobilier Levallois-Perret (92300) : DPE, amiante, Pack Vente. Quartier résidentiel premium, immeubles haussmanniens et modernes. Dès 130 €.",
    intro:
      "Levallois-Perret est l'une des communes les plus denses d'Europe avec un parc immobilier premium très convoité. Up Diag intervient sur tous les types de biens levalloisiens : immeubles haussmanniens du Centre, résidences Art Déco et immeubles des années 1990-2000 du quartier Eiffel.",
    parc: "Le parc de Levallois-Perret compte environ 36 000 logements très majoritairement en copropriété. Le centre-ville concentre les immeubles haussmanniens et bourgeois d'avant 1920. Les rénovations ont été nombreuses, ce qui peut faciliter le classement DPE mais impose le diagnostic amiante pour les travaux datant d'avant 1997.",
    quartiers: ["Centre-Ville", "Eiffel", "Chaptal", "Front-de-Seine", "Collange"],
    prixM2: "10 100 €/m²",
    diagnostics: [
      "CREP plomb systématique (centre-ville avant 1949)",
      "Amiante : immeubles 1920-1960 (nombreuses rénovations post-1997)",
      "Pack Vente premium pour les ventes en copropriété",
      "Loi Carrez : quasi-systématique (100 % de copropriété)",
    ],
    heroImage:
      "/img/readdy/city-levallois.jpg",
  },
  {
    slug: "issy-les-moulineaux",
    nom: "Issy-les-Moulineaux",
    deptCode: "92",
    deptNom: "Hauts-de-Seine",
    deptSlug: "hauts-de-seine-92",
    h1: "Diagnostic immobilier à Issy-les-Moulineaux (92) — DPE & Pack Location",
    metaTitle: "Diagnostic Immobilier Issy-les-Moulineaux 92 | DPE Val de Seine | Up Diag",
    metaDesc:
      "Diagnostic immobilier Issy-les-Moulineaux (92130) : DPE, amiante, Pack Vente. Val de Seine, écoquartier et immeubles modernes. Dès 130 €.",
    intro:
      "Issy-les-Moulineaux est une ville dynamique du sud-ouest parisien qui a connu une forte mutation urbaine. L'écoquartier du Fort et la ZAC Val de Seine offrent un parc immobilier moderne avec des DPE favorables, tandis que le vieil Issy conserve un bâti plus ancien nécessitant les diagnostics traditionnels.",
    parc: "Le parc d'Issy-les-Moulineaux s'est profondément renouvelé depuis 20 ans. Le Val de Seine et le Fort offrent des logements récents (RT 2005, RT 2012) souvent classés A-C au DPE. Le centre historique et les Épinettes conservent des immeubles 1920-1970 nécessitant CREP plomb et diagnostic amiante.",
    quartiers: ["Val de Seine", "Fort d'Issy", "Centre-Ville", "Les Épinettes", "Corentin Celton"],
    prixM2: "9 300 €/m²",
    diagnostics: [
      "DPE favorable sur le parc neuf (Val de Seine, Fort)",
      "CREP plomb pour les biens centre-ville avant 1949",
      "Amiante : copros 1960-1980 des Épinettes",
      "Pack Location : forte demande locative (proximité Paris 15e)",
    ],
    heroImage:
      "/img/readdy/city-issy.jpg",
  },

  // ═════════ 93 — Seine-Saint-Denis ═════════
  {
    slug: "saint-denis",
    nom: "Saint-Denis",
    deptCode: "93",
    deptNom: "Seine-Saint-Denis",
    deptSlug: "seine-saint-denis-93",
    h1: "Diagnostic immobilier à Saint-Denis (93) — DPE & Pack Location",
    metaTitle: "Diagnostic Immobilier Saint-Denis 93 | DPE Plaine Saint-Denis | Up Diag",
    metaDesc:
      "Diagnostic immobilier Saint-Denis (93200) : DPE, amiante, plomb, permis de louer. Forte demande locative, parc ancien et ZAC récentes. Dès 130 €.",
    intro:
      "Saint-Denis est une ville dense en pleine mutation avec le développement de la Plaine Saint-Denis et la préparation des Jeux Olympiques 2024. Up Diag intervient dans tous les quartiers pour vos diagnostics immobiliers, avec une attention particulière au permis de louer obligatoire dans cette commune.",
    parc: "Le parc dionysien se partage entre centre-ville ancien (immeubles de rapport 1880-1930), cités des années 1960-1980 et quartiers récents de la Plaine Saint-Denis. La commune est soumise au <strong>permis de louer</strong> : toute mise en location doit faire l'objet d'une autorisation préalable pour lutter contre l'habitat insalubre.",
    quartiers: ["Centre-Ville", "Plaine Saint-Denis", "Pleyel - Confluence", "Franc-Moisin", "Basilique"],
    prixM2: "4 300 €/m²",
    diagnostics: [
      "CREP plomb obligatoire sur le centre ancien (avant 1949)",
      "Amiante parties privatives : immeubles 1960-1997 nombreux",
      "Pack Location complet (permis de louer requis)",
      "DPE : enjeu majeur sur le parc ancien — beaucoup de F/G à rénover",
    ],
    heroImage:
      "/img/readdy/city-saintdenis.jpg",
  },
  {
    slug: "montreuil",
    nom: "Montreuil",
    deptCode: "93",
    deptNom: "Seine-Saint-Denis",
    deptSlug: "seine-saint-denis-93",
    h1: "Diagnostic immobilier à Montreuil (93) — DPE Croix-de-Chavaux",
    metaTitle: "Diagnostic Immobilier Montreuil 93 | DPE Bas Montreuil | Up Diag",
    metaDesc:
      "Diagnostic immobilier Montreuil (93100) : DPE, amiante, Pack Vente. Bas Montreuil artistique, Haut Montreuil pavillonnaire. Dès 130 €.",
    intro:
      "Montreuil est une commune de 110 000 habitants très prisée des jeunes actifs et familles parisiennes. Le parc immobilier oscille entre immeubles de rapport du Bas Montreuil et pavillons anciens du Haut Montreuil. Up Diag intervient dans tous les quartiers, avec une attention particulière au diagnostic plomb sur les biens anciens.",
    parc: "Le Bas Montreuil (proche Paris 20e) concentre des immeubles 1880-1930 et des ateliers reconvertis en loft, avec beaucoup de biens avant 1949 concernés par le CREP. Le Haut Montreuil présente un tissu pavillonnaire dense, avec des maisons individuelles des années 1920-1950 nécessitant souvent des diagnostics électricité et gaz obligatoires.",
    quartiers: ["Bas Montreuil", "Croix-de-Chavaux", "Centre-Ville", "Solidarité - Carnot", "Bel-Air - Ruffins"],
    prixM2: "5 100 €/m²",
    diagnostics: [
      "CREP plomb : obligatoire Bas Montreuil (immeubles avant 1949)",
      "Amiante : ateliers reconvertis et immeubles 1960-1997",
      "Électricité / gaz : pavillons du Haut Montreuil (&gt; 15 ans)",
      "Pack Vente complet pour les lofts",
    ],
    heroImage:
      "/img/readdy/city-montreuil.jpg",
  },
  {
    slug: "aubervilliers",
    nom: "Aubervilliers",
    deptCode: "93",
    deptNom: "Seine-Saint-Denis",
    deptSlug: "seine-saint-denis-93",
    h1: "Diagnostic immobilier à Aubervilliers (93) — DPE & Pack Location",
    metaTitle: "Diagnostic Immobilier Aubervilliers 93 | DPE Plaine | Up Diag",
    metaDesc:
      "Diagnostic immobilier Aubervilliers (93300) : DPE, amiante, plomb, permis de louer. Ville en mutation, ZAC Plaine. Intervention 48h.",
    intro:
      "Aubervilliers, commune de 90 000 habitants limitrophe de Paris, connaît une forte mutation urbaine avec la ZAC Canal-Porte d'Aubervilliers et le Campus Condorcet. Up Diag intervient pour tous les diagnostics immobiliers, en tenant compte du permis de louer obligatoire dans cette commune.",
    parc: "Le parc immobilier d'Aubervilliers mélange anciens immeubles de rapport du centre (avant 1949), grands ensembles des années 1960 de la Maladrerie et logements récents de la Plaine. La ville est soumise au <strong>permis de louer</strong>, rendant le diagnostic complet obligatoire avant toute mise en location.",
    quartiers: ["Centre-Ville", "Canal - Porte d'Aubervilliers", "Maladrerie - Émile Dubois", "Quatre-Chemins", "Landy"],
    prixM2: "4 000 €/m²",
    diagnostics: [
      "CREP plomb obligatoire (parc ancien avant 1949)",
      "Amiante parties privatives (grands ensembles 1960-1997)",
      "Pack Location complet (permis de louer)",
      "DPE : beaucoup de passoires thermiques F/G à rénover",
    ],
    heroImage:
      "/img/readdy/city-aubervilliers.jpg",
  },

  // ═════════ 94 — Val-de-Marne ═════════
  {
    slug: "creteil",
    nom: "Créteil",
    deptCode: "94",
    deptNom: "Val-de-Marne",
    deptSlug: "val-de-marne-94",
    h1: "Diagnostic immobilier à Créteil (94) — DPE Préfecture",
    metaTitle: "Diagnostic Immobilier Créteil 94 | DPE Pack Vente | Up Diag",
    metaDesc:
      "Diagnostic immobilier Créteil (94000) : DPE, amiante, plomb. Préfecture du Val-de-Marne, grandes copropriétés années 1970. Dès 130 €.",
    intro:
      "Créteil, préfecture du Val-de-Marne, se caractérise par un urbanisme des années 1970 marqué : immeubles résidentiels autour du Mont-Mesly, du Palais et du Lac. Up Diag intervient sur tous les types de biens cristoliens, avec une expertise reconnue sur les grandes copropriétés de la commune.",
    parc: "Le parc de Créteil est dominé par les grandes copropriétés des années 1960-1980 (Mont-Mesly, Palais, Lac). Ces immeubles requièrent fréquemment des diagnostics amiante parties privatives et DPE collectifs lorsqu'ils comptent plus de 50 lots. Quelques quartiers plus anciens (Village, Bleuets) conservent du pavillonnaire avant 1949.",
    quartiers: ["Mont-Mesly", "Palais", "Lac", "Village", "Brèche"],
    prixM2: "4 500 €/m²",
    diagnostics: [
      "Amiante parties privatives : copros 1960-1997 (Mont-Mesly, Palais)",
      "DPE collectif : copropriétés de plus de 50 lots",
      "Pack Vente complet pour appartements en copro",
      "CREP plomb : secteur Village (pavillons avant 1949)",
    ],
    heroImage:
      "/img/readdy/city-creteil.jpg",
  },
  {
    slug: "vincennes",
    nom: "Vincennes",
    deptCode: "94",
    deptNom: "Val-de-Marne",
    deptSlug: "val-de-marne-94",
    h1: "Diagnostic immobilier à Vincennes (94) — DPE & Pack Vente premium",
    metaTitle: "Diagnostic Immobilier Vincennes 94 | DPE Château Bois | Up Diag",
    metaDesc:
      "Diagnostic immobilier Vincennes (94300) : DPE, amiante, plomb, Pack Vente. Cadre de vie premium, bois de Vincennes. Dès 130 €.",
    intro:
      "Vincennes est une ville résidentielle prisée grâce à son cadre de vie exceptionnel (château, bois de Vincennes) et sa proximité immédiate avec Paris 12e. Le parc immobilier vincennois est majoritairement ancien, avec de nombreuses copropriétés haussmanniennes et Art Déco nécessitant un suivi diagnostique rigoureux.",
    parc: "Vincennes compte environ 49 000 habitants et son parc immobilier est l'un des plus qualitatifs du 94. Les immeubles haussmanniens du centre (autour de la place Diderot et de la rue de Montreuil) côtoient des résidences Art Déco et des immeubles post-1950. Beaucoup de biens d'avant 1949 imposent le CREP plomb.",
    quartiers: ["Centre-Ville - Diderot", "Château", "Nogent", "République", "Bois"],
    prixM2: "8 900 €/m²",
    diagnostics: [
      "CREP plomb systématique (centre haussmannien avant 1949)",
      "Pack Vente premium pour appartements en copropriété",
      "DPE : enjeu sur le parc haussmannien peu isolé",
      "Loi Carrez obligatoire (quasi-totalité en copro)",
    ],
    heroImage:
      "/img/readdy/city-vincennes.jpg",
  },
  {
    slug: "saint-maur-des-fosses",
    nom: "Saint-Maur-des-Fossés",
    deptCode: "94",
    deptNom: "Val-de-Marne",
    deptSlug: "val-de-marne-94",
    h1: "Diagnostic immobilier à Saint-Maur-des-Fossés (94) — DPE Pavillons",
    metaTitle: "Diagnostic Immobilier Saint-Maur 94 | DPE Pack Vente | Up Diag",
    metaDesc:
      "Diagnostic immobilier Saint-Maur-des-Fossés (94100) : DPE, amiante, Pack Vente pavillons. Cadre de vie résidentiel, bord de Marne. Dès 130 €.",
    intro:
      "Saint-Maur-des-Fossés est une commune résidentielle huppée du Val-de-Marne, réputée pour son cadre de vie au bord de la Marne et ses pavillons anciens. Up Diag intervient sur l'ensemble des diagnostics immobiliers à Saint-Maur, avec une expertise particulière sur les maisons individuelles et pavillons 1920-1960.",
    parc: "Le parc de Saint-Maur est dominé par le pavillonnaire ancien (années 1920-1960) et quelques petites copropriétés centre-ville. Les maisons sont souvent très individuelles avec des installations électriques et gaz datant d'avant 1990, imposant les diagnostics réglementaires. Le cadre en bord de Marne attire une population premium.",
    quartiers: ["Vieux Saint-Maur", "La Pie", "Parc", "Champignol", "Adamville"],
    prixM2: "6 800 €/m²",
    diagnostics: [
      "Pack Vente complet pour pavillons (DPE + Amiante + Plomb + Élec + Gaz)",
      "CREP plomb pour biens avant 1949",
      "Électricité : installations pavillonnaires anciennes",
      "Gaz : chaudières et appareils pavillonnaires",
    ],
    heroImage:
      "/img/readdy/city-saintmaur.jpg",
  },

  // ═════════ 78 — Yvelines ═════════
  {
    slug: "versailles",
    nom: "Versailles",
    deptCode: "78",
    deptNom: "Yvelines",
    deptSlug: "yvelines-78",
    h1: "Diagnostic immobilier à Versailles (78) — DPE Pack Vente classique",
    metaTitle: "Diagnostic Immobilier Versailles 78 | DPE Notre-Dame | Up Diag",
    metaDesc:
      "Diagnostic immobilier Versailles (78000) : DPE, amiante, plomb, Pack Vente. Hôtels particuliers et immeubles classiques. Dès 130 €.",
    intro:
      "Versailles est une ville royale prestigieuse qui abrite un parc immobilier unique : hôtels particuliers XVIIIe, immeubles classiques du quartier Notre-Dame, résidences Art Déco. Up Diag maîtrise les spécificités du bâti versaillais, souvent inscrit en secteur sauvegardé, et réalise tous les diagnostics obligatoires avec le soin que ces biens exigent.",
    parc: "Le parc versaillais est exceptionnellement qualitatif : hôtels particuliers du quartier Saint-Louis et Notre-Dame (XVIIIe siècle), immeubles bourgeois des années 1880-1920, résidences Art Déco. La plupart des biens sont d'avant 1949 et imposent le CREP plomb. Les contraintes patrimoniales (secteur sauvegardé, ABF) impactent les travaux de rénovation énergétique.",
    quartiers: ["Notre-Dame", "Saint-Louis", "Clagny-Glatigny", "Chantiers", "Porchefontaine"],
    prixM2: "7 400 €/m²",
    diagnostics: [
      "CREP plomb systématique (immeubles XVIIIe-XIXe)",
      "Pack Vente complet haut de gamme",
      "DPE difficile à optimiser (contraintes ABF)",
      "Loi Carrez : obligatoire pour ventes en copro",
    ],
    heroImage:
      "/img/readdy/city-versailles.jpg",
  },
  {
    slug: "saint-germain-en-laye",
    nom: "Saint-Germain-en-Laye",
    deptCode: "78",
    deptNom: "Yvelines",
    deptSlug: "yvelines-78",
    h1: "Diagnostic immobilier à Saint-Germain-en-Laye (78) — DPE Pack Vente",
    metaTitle: "Diagnostic Immobilier Saint-Germain-en-Laye 78 | DPE | Up Diag",
    metaDesc:
      "Diagnostic immobilier Saint-Germain-en-Laye (78100) : DPE, amiante, Pack Vente. Ville royale, cadre résidentiel premium. Dès 130 €.",
    intro:
      "Saint-Germain-en-Laye offre un cadre de vie résidentiel prestigieux à l'ouest de Paris, avec son château, sa forêt et ses quartiers bourgeois. Up Diag intervient pour tous vos diagnostics immobiliers avec une expertise spécifique au parc saint-germanois : hôtels particuliers, immeubles classiques et pavillons anciens.",
    parc: "Le parc de Saint-Germain mélange immeubles bourgeois du centre-ville (1850-1920), pavillonnaire ancien et résidences récentes en périphérie. Le centre-ville impose souvent le CREP plomb et l'amiante pour les travaux réalisés avant 1997. Les hôtels particuliers requièrent des diagnostics techniques rigoureux.",
    quartiers: ["Centre-Ville", "Pereire", "Alsace-Lorraine", "Debussy", "Bel-Air"],
    prixM2: "7 100 €/m²",
    diagnostics: [
      "CREP plomb : centre-ville (biens avant 1949)",
      "Pack Vente premium pour hôtels particuliers",
      "Amiante : copros 1960-1997",
      "Loi Carrez (copropriétés centre)",
    ],
    heroImage:
      "/img/readdy/city-saintgermain.jpg",
  },
  {
    slug: "mantes-la-jolie",
    nom: "Mantes-la-Jolie",
    deptCode: "78",
    deptNom: "Yvelines",
    deptSlug: "yvelines-78",
    h1: "Diagnostic immobilier à Mantes-la-Jolie (78) — DPE Pack Location",
    metaTitle: "Diagnostic Immobilier Mantes-la-Jolie 78 | DPE | Up Diag",
    metaDesc:
      "Diagnostic immobilier Mantes-la-Jolie (78200) : DPE, amiante, plomb. Parc collectif des années 1960-1980, quartiers en rénovation. Dès 130 €.",
    intro:
      "Mantes-la-Jolie, sous-préfecture des Yvelines, concentre un parc immobilier marqué par l'urbanisme des années 1960-1980. La commune connaît une forte dynamique de rénovation urbaine (ANRU), ce qui rend les diagnostics amiante et DPE particulièrement importants. Up Diag intervient dans tous les quartiers mantais.",
    parc: "Le parc mantais se partage entre les grandes copropriétés Val Fourré (années 1960-70, en cours de rénovation ANRU), centre-ville ancien et pavillonnaire. Les copropriétés anciennes imposent des diagnostics amiante et DPE collectifs, tandis que le pavillonnaire nécessite les diagnostics électricité et gaz.",
    quartiers: ["Val Fourré", "Gassicourt", "Centre-Ville", "Les Brouets", "Les Martraits"],
    prixM2: "2 800 €/m²",
    diagnostics: [
      "Amiante parties privatives (copros ANRU 1960-1997)",
      "DPE : fort enjeu (beaucoup de F/G)",
      "Pack Location (marché locatif dynamique)",
      "CREP plomb : secteur centre ancien",
    ],
    heroImage:
      "/img/readdy/city-mantes.jpg",
  },

  // ═════════ 91 — Essonne ═════════
  {
    slug: "evry-courcouronnes",
    nom: "Évry-Courcouronnes",
    deptCode: "91",
    deptNom: "Essonne",
    deptSlug: "essonne-91",
    h1: "Diagnostic immobilier à Évry-Courcouronnes (91) — DPE Préfecture",
    metaTitle: "Diagnostic Immobilier Évry-Courcouronnes 91 | DPE | Up Diag",
    metaDesc:
      "Diagnostic immobilier Évry-Courcouronnes (91000) : DPE, amiante, Pack Vente. Préfecture de l'Essonne, ville nouvelle. Dès 130 €.",
    intro:
      "Évry-Courcouronnes, préfecture de l'Essonne, est une ville nouvelle créée dans les années 1970. Son parc immobilier est marqué par l'urbanisme de cette période : grandes copropriétés, résidences modernes et quartiers récents. Up Diag intervient sur tous les diagnostics à Évry avec une connaissance fine des biens collectifs.",
    parc: "Le parc d'Évry-Courcouronnes est dominé par les résidences ville nouvelle des années 1970-1990. Ces immeubles requièrent souvent le diagnostic amiante parties privatives et le DPE collectif pour les grandes copropriétés. Le reste de la commune compte des quartiers plus récents avec des DPE plus favorables.",
    quartiers: ["Bois Sauvage", "Pyramides", "Champs Élysées", "Canal", "Aunettes"],
    prixM2: "2 900 €/m²",
    diagnostics: [
      "Amiante parties privatives (immeubles ville nouvelle 1970-1997)",
      "DPE collectif (grandes copropriétés)",
      "Pack Vente complet",
      "Pack Location (proximité université)",
    ],
    heroImage:
      "/img/readdy/city-evry.jpg",
  },
  {
    slug: "massy",
    nom: "Massy",
    deptCode: "91",
    deptNom: "Essonne",
    deptSlug: "essonne-91",
    h1: "Diagnostic immobilier à Massy (91) — DPE & Pack Vente",
    metaTitle: "Diagnostic Immobilier Massy 91 | DPE TGV | Up Diag",
    metaDesc:
      "Diagnostic immobilier Massy (91300) : DPE, amiante, Pack Vente. Pôle TGV, quartiers récents et pavillonnaire ancien. Dès 130 €.",
    intro:
      "Massy est une commune dynamique de l'Essonne, reconnue pour son pôle TGV et ses quartiers récents. Le parc immobilier se renouvelle fortement avec de nouveaux écoquartiers offrant des DPE favorables. Up Diag intervient à Massy pour tous les types de diagnostics, du pavillon ancien à l'appartement neuf.",
    parc: "Le parc massicois mélange pavillonnaire ancien (Massy-Palaiseau, Massy-Verrières), copropriétés des années 1960-1980 (Opéra, Vilmorin) et quartiers récents (Atlantis, Massy-Europe). Les résidences neuves offrent des DPE A à C, tandis que les copros anciennes nécessitent amiante et DPE collectif.",
    quartiers: ["Atlantis", "Vilmorin", "Opéra", "Atlantis", "Villaine"],
    prixM2: "4 100 €/m²",
    diagnostics: [
      "DPE : contrasté (bon sur le neuf, F/G sur l'ancien)",
      "Amiante : copropriétés 1960-1997",
      "Pack Vente complet (secteur dynamique)",
      "Pack Location (forte demande grâce au TGV)",
    ],
    heroImage:
      "/img/readdy/city-massy.jpg",
  },

  // ═════════ 77 — Seine-et-Marne ═════════
  {
    slug: "meaux",
    nom: "Meaux",
    deptCode: "77",
    deptNom: "Seine-et-Marne",
    deptSlug: "seine-et-marne-77",
    h1: "Diagnostic immobilier à Meaux (77) — DPE & Pack Vente",
    metaTitle: "Diagnostic Immobilier Meaux 77 | DPE Centre historique | Up Diag",
    metaDesc:
      "Diagnostic immobilier Meaux (77100) : DPE, amiante, plomb, Pack Vente. Centre historique et maisons individuelles. Dès 130 €.",
    intro:
      "Meaux, sous-préfecture de Seine-et-Marne, concentre un parc immobilier varié : centre historique ancien, maisons individuelles nombreuses et quartiers résidentiels. Up Diag intervient à Meaux pour tous les diagnostics immobiliers, avec une expertise sur les maisons individuelles qui constituent une forte part du marché.",
    parc: "Le parc meldois se partage entre centre-ville historique (immeubles anciens avant 1949), zones pavillonnaires des quartiers Beauval et Pierre Collinet, et quelques résidences modernes. La forte présence de maisons individuelles impose les diagnostics électricité, gaz et parfois assainissement non collectif.",
    quartiers: ["Centre-Ville", "Beauval", "Pierre Collinet", "Luxembourg", "Chantereine"],
    prixM2: "2 700 €/m²",
    diagnostics: [
      "Pack Vente complet pour pavillons",
      "CREP plomb (centre historique avant 1949)",
      "Électricité / gaz : pavillonnaire ancien",
      "Assainissement non collectif (zones péri-urbaines)",
    ],
    heroImage:
      "/img/readdy/city-meaux.jpg",
  },
  {
    slug: "melun",
    nom: "Melun",
    deptCode: "77",
    deptNom: "Seine-et-Marne",
    deptSlug: "seine-et-marne-77",
    h1: "Diagnostic immobilier à Melun (77) — DPE Pack Vente",
    metaTitle: "Diagnostic Immobilier Melun 77 | DPE Préfecture | Up Diag",
    metaDesc:
      "Diagnostic immobilier Melun (77000) : DPE, amiante, Pack Vente. Préfecture de Seine-et-Marne, parc varié. Dès 130 €.",
    intro:
      "Melun, préfecture de Seine-et-Marne, offre un parc immobilier très varié : centre-ville ancien sur l'île Saint-Étienne, résidences post-guerre, quartiers pavillonnaires et ensembles récents. Up Diag intervient dans toute la commune avec un délai d'intervention de 48h.",
    parc: "Le parc melunais se répartit entre l'île Saint-Étienne (centre historique avant 1949), les quartiers Nord et Plateau avec des copropriétés des années 1960-1980, et des zones pavillonnaires en périphérie. Chaque typologie impose ses diagnostics spécifiques.",
    quartiers: ["Centre-Ville", "Saint-Étienne", "Plateau Nord", "Saints-Pères", "Almont"],
    prixM2: "2 850 €/m²",
    diagnostics: [
      "CREP plomb : île Saint-Étienne (avant 1949)",
      "Amiante : copros 1960-1997",
      "Pack Vente pour pavillons de la périphérie",
      "Assainissement non collectif (zones rurales)",
    ],
    heroImage:
      "/img/readdy/city-melun.jpg",
  },

  // ═════════ 95 — Val-d'Oise ═════════
  {
    slug: "cergy",
    nom: "Cergy",
    deptCode: "95",
    deptNom: "Val-d'Oise",
    deptSlug: "val-d-oise-95",
    h1: "Diagnostic immobilier à Cergy (95) — DPE & Pack Location",
    metaTitle: "Diagnostic Immobilier Cergy 95 | DPE Cergy-Pontoise | Up Diag",
    metaDesc:
      "Diagnostic immobilier Cergy (95000) : DPE, amiante, Pack Location. Ville nouvelle de Cergy-Pontoise. Dès 130 €.",
    intro:
      "Cergy, ville nouvelle de l'agglomération Cergy-Pontoise, présente un parc immobilier des années 1970-1990 avec quelques quartiers plus récents (ZAC Grand Centre). Up Diag intervient dans toute la commune avec une maîtrise particulière des copropriétés ville nouvelle.",
    parc: "Le parc cergyssois est dominé par les résidences ville nouvelle des années 1970-1990 : Axe Majeur, Plaine des Linandes, Saint-Christophe. Ces immeubles requièrent souvent le diagnostic amiante parties privatives et le DPE collectif. Les quartiers récents (Grand Centre) offrent des DPE plus favorables.",
    quartiers: ["Axe Majeur", "Plaine des Linandes", "Saint-Christophe", "Grand Centre", "Hauts de Cergy"],
    prixM2: "3 100 €/m²",
    diagnostics: [
      "Amiante parties privatives (copros 1970-1997)",
      "DPE collectif (grandes copropriétés ville nouvelle)",
      "Pack Location (proximité université Cergy-Paris)",
      "Pack Vente pour résidences récentes",
    ],
    heroImage:
      "/img/readdy/city-cergy.jpg",
  },
  {
    slug: "argenteuil",
    nom: "Argenteuil",
    deptCode: "95",
    deptNom: "Val-d'Oise",
    deptSlug: "val-d-oise-95",
    h1: "Diagnostic immobilier à Argenteuil (95) — DPE Pack Location",
    metaTitle: "Diagnostic Immobilier Argenteuil 95 | DPE | Up Diag",
    metaDesc:
      "Diagnostic immobilier Argenteuil (95100) : DPE, amiante, plomb. Parc contrasté, proximité Paris. Dès 130 €.",
    intro:
      "Argenteuil, troisième ville d'Île-de-France par sa population, présente un parc immobilier extrêmement contrasté : centre ancien, grands ensembles, pavillons, et nouveaux écoquartiers (Champion, Berges de Seine). Up Diag intervient dans tous les quartiers d'Argenteuil avec une connaissance fine du bâti francilien.",
    parc: "Le parc argenteuillais mélange centre-ville ancien (avant 1949), grands ensembles Val d'Argent et Orgemont (1960-1980), et pavillonnaire 1920-1960. Les rénovations urbaines ANRU transforment certains quartiers. Le Pack Location est très demandé en raison de la forte activité locative.",
    quartiers: ["Centre-Ville", "Val d'Argent", "Orgemont", "Coteaux", "Champion"],
    prixM2: "3 200 €/m²",
    diagnostics: [
      "CREP plomb : centre ancien (avant 1949)",
      "Amiante parties privatives (Val d'Argent, Orgemont 1960-1997)",
      "Pack Location (forte demande locative)",
      "DPE : enjeu majeur (nombreux F/G à rénover)",
    ],
    heroImage:
      "/img/readdy/city-argenteuil.jpg",
  },
];
